// Lifted and adapted from Mantine V9.1.1
// @TODO: Replace with native Mantine component once we upgrade to Mantine V9

import { forwardRef, useCallback } from 'react';
import { Box, UnstyledButton } from '@mantine/core';

import { IconChevronLeft } from '~/domains/design-system/icons/IconChevronLeft';
import { IconChevronRight } from '~/domains/design-system/icons/IconChevronRight';

import { useScroller } from './useScroller';
import useStyles from './Scroller.styles';

export interface ScrollerProps {
  /** Content to display */
  children: React.ReactNode;

  /** Amount of pixels to scroll when clicking the control buttons, `200` by default */
  scrollAmount?: number;

  /** Determines whether start control should always be visible regardless of scroll position, `false` by default */
  showStartControl?: boolean;

  /** Determines whether end control should always be visible regardless of scroll position, `false` by default */
  showEndControl?: boolean;

  /** Determines whether content can be scrolled by dragging with mouse, `true` by default */
  draggable?: boolean;

  /** Background color for the gradient fade on controls, 'white' by default */
  backgroundColor?: string;

  controlVariant?: 'plain' | 'floating';

  /** Determines whether the controls sit on a gradient fade of `backgroundColor`, `true` by default */
  withFade?: boolean;

  withControls?: boolean;

  fillWidth?: boolean;
}

// Using forwardRef to surface the scrollable container ref to the parent component
// Used for auto-scroll into view with useScrollIntoView hook
export const Scroller = forwardRef<HTMLDivElement, ScrollerProps>(
  (
    {
      showStartControl,
      showEndControl,
      draggable,
      scrollAmount,
      children,
      backgroundColor = 'white',
      controlVariant = 'plain',
      withFade = true,
      withControls = true,
      fillWidth = false,
    },
    ref
  ) => {
    const { classes } = useStyles({ backgroundColor, controlVariant, withFade, fillWidth });

    const scroller = useScroller({
      scrollAmount,
      draggable,
    });
    const { ref: scrollerRef } = scroller;

    const setContainerRef = useCallback(
      (node: HTMLDivElement | null) => {
        scrollerRef(node);
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [scrollerRef, ref]
    );

    const showStart = showStartControl || scroller.canScrollStart;
    const showEnd = showEndControl || scroller.canScrollEnd;

    return (
      <Box className={classes.root}>
        {withControls && (
          <UnstyledButton
            className={classes.control}
            onClick={scroller.scrollStart}
            data-position="start"
            data-hidden={!showStart || undefined}
            aria-label="Scroll left"
            tabIndex={showStart ? 0 : -1}
          >
            {controlVariant === 'floating' ? (
              <span className={classes.chevronChip}>
                <IconChevronLeft className={classes.chevron} />
              </span>
            ) : (
              <IconChevronLeft className={classes.chevron} />
            )}
          </UnstyledButton>
        )}

        <div
          className={classes.container}
          ref={setContainerRef}
          role="presentation"
          data-draggable={draggable || undefined}
          {...scroller.dragHandlers}
        >
          <div className={classes.content}>{children}</div>
        </div>

        {withControls && (
          <UnstyledButton
            className={classes.control}
            onClick={scroller.scrollEnd}
            data-position="end"
            data-hidden={!showEnd || undefined}
            aria-label="Scroll right"
            tabIndex={showEnd ? 0 : -1}
          >
            {controlVariant === 'floating' ? (
              <span className={classes.chevronChip}>
                <IconChevronRight className={classes.chevron} />
              </span>
            ) : (
              <IconChevronRight className={classes.chevron} />
            )}
          </UnstyledButton>
        )}
      </Box>
    );
  }
);

Scroller.displayName = 'Scroller';
